import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedModule } from "../shared.module";

interface WarehouseOrder {
  id: number;
  customerName: string;
  description: string;
  status: 'toimitettu' | 'kesken' | 'peruttu' | 'odottaa';
  itemCount: number;
  orderDate: Date;
  deliveryDate?: Date;
  priority: 'high' | 'normal' | 'low';
  location: string;
}

@Component({
    selector: 'app-example-component',
    templateUrl: './example-component.template.html',
    styleUrls: ['./example-component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SharedModule]
})
export class ExampleComponent implements OnInit {
  displayedColumns: string[] = ['select', 'status', 'customer', 'items', 'dates', 'actions'];
  dataSource = new MatTableDataSource<WarehouseOrder>();
  selection = new SelectionModel<WarehouseOrder>(true, []);

  // Form controls for filters and forms
  searchControl = new FormControl('');
  statusFilter = new FormControl([]);
  priorityFilter = new FormControl('');
  viewModeControl = new FormControl('table');

  // Order form
  orderForm: FormGroup;

  isLoading = false;
  isEmpty = false;
  showDebug = false; // Set to true in development

  // Finnish business context example data
  sampleOrders: WarehouseOrder[] = [
    {
      id: 1001,
      customerName: 'Rakennusliike Virtanen Oy',
      description: 'Betonielementit - Kerrostalo projekti',
      status: 'toimitettu',
      itemCount: 24,
      orderDate: new Date('2024-01-15'),
      deliveryDate: new Date('2024-01-18'),
      priority: 'high',
      location: 'Helsinki, Punavuori'
    },
    {
      id: 1002,
      customerName: 'Infratek Suomi',
      description: 'Tie- ja katurakentaminen materiaalit',
      status: 'kesken',
      itemCount: 12,
      orderDate: new Date('2024-01-16'),
      deliveryDate: new Date('2024-01-20'),
      priority: 'normal',
      location: 'Espoo, Otaniemi'
    },
    {
      id: 1003,
      customerName: 'Peab Finland Oy',
      description: 'Sillan kunnostus - erikoismateriaalit',
      status: 'odottaa',
      itemCount: 8,
      orderDate: new Date('2024-01-17'),
      priority: 'high',
      location: 'Tampere, Keskusta'
    },
    {
      id: 1004,
      customerName: 'YIT Suomi Oy',
      description: 'Asuinkiinteistö - sisustusmateriaalit',
      status: 'peruttu',
      itemCount: 0,
      orderDate: new Date('2024-01-14'),
      priority: 'low',
      location: 'Turku, Kupittaa'
    }
  ];

  debugInfo = {
    component: 'RaksaInfo V4 Example Component',
    timestamp: new Date(),
    dataCount: 0,
    theme: 'Material 3 Light',
    module: 'Warehouse Management',
    user: 'Example User',
    permissions: ['view_orders', 'edit_orders', 'create_orders']
  };

  constructor(private fb: FormBuilder, private snackBar: MatSnackBar) {
    this.dataSource.data = this.sampleOrders;
    this.debugInfo.dataCount = this.sampleOrders.length;

    // Initialize the order form
    this.orderForm = this.fb.group({
      basicInfo: this.fb.group({
        customerName: ['', Validators.required],
        description: ['', Validators.required],
        priority: ['normal'],
        itemCount: [1, [Validators.required, Validators.min(1)]]
      }),
      locationInfo: this.fb.group({
        location: ['', Validators.required],
        deliveryDate: [''],
        deliveryTime: ['']
      })
    });
  }

  ngOnInit(): void {
    // Simulate loading state
    this.refreshData();
  }

  refreshData(): void {
    this.isLoading = true;
    // Simulate API call
    setTimeout(() => {
      this.isLoading = false;
      this.dataSource.data = this.sampleOrders;
      this.isEmpty = this.sampleOrders.length === 0;
    }, 1500);
  }

  addNewOrder(): void {
    console.log('Uusi tilaus - Add new order clicked');
    // Implement add functionality
  }

  viewOrder(order: WarehouseOrder): void {
    console.log('Näytä tilaus - View order:', order);
    // Implement view functionality
  }

  editOrder(order: WarehouseOrder): void {
    console.log('Muokkaa tilausta - Edit order:', order);
    // Implement edit functionality
  }

  deleteOrder(order: WarehouseOrder): void {
    console.log('Poista tilaus - Delete order:', order);
    // Implement delete functionality with confirmation
  }

  markAsDelivered(order: WarehouseOrder): void {
    console.log('Merkitse toimitetuksi - Mark as delivered:', order);
    order.status = 'toimitettu';
    order.deliveryDate = new Date();
  }

  exportToExcel(): void {
    console.log('Vie Excel-tiedostoon - Export to Excel');
    // Implement Excel export
  }

  printReport(): void {
    console.log('Tulosta raportti - Print report');
    // Implement printing
  }

  getStatusClass(status: string): string {
    const statusMap: { [key: string]: string } = {
      'toimitettu': 'delivered',
      'kesken': 'pending',
      'peruttu': 'cancelled',
      'odottaa': 'waiting'
    };
    return statusMap[status] || 'pending';
  }

  getStatusDisplayText(status: string): string {
    const displayMap: { [key: string]: string } = {
      'toimitettu': 'Toimitettu',
      'kesken': 'Kesken',
      'peruttu': 'Peruutettu',
      'odottaa': 'Odottaa'
    };
    return displayMap[status] || status;
  }

  getPriorityIcon(priority: string): string {
    const iconMap: { [key: string]: string } = {
      'high': 'priority_high',
      'normal': 'remove',
      'low': 'keyboard_arrow_down'
    };
    return iconMap[priority] || 'remove';
  }

  // Additional methods for template functionality

  // Form group getters for template
  get basicInfoForm(): FormGroup {
    return this.orderForm.get('basicInfo') as FormGroup;
  }

  get locationInfoForm(): FormGroup {
    return this.orderForm.get('locationInfo') as FormGroup;
  }

  onSearchChange(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  getOrderCount(status: string): number {
    return this.sampleOrders.filter(order => order.status === status).length;
  }

  getStatusIcon(status: string): string {
    const iconMap: { [key: string]: string } = {
      'toimitettu': 'check_circle',
      'kesken': 'schedule',
      'peruttu': 'cancel',
      'odottaa': 'hourglass_empty'
    };
    return iconMap[status] || 'help';
  }

  // Selection methods for table
  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected() ?
      this.selection.clear() :
      this.dataSource.data.forEach(row => this.selection.select(row));
  }

  // Additional action methods
  duplicateOrder(order: WarehouseOrder): void {
    console.log('Monista tilaus - Duplicate order:', order);
    this.snackBar.open('Tilaus kopioitu', 'Sulje', { duration: 3000 });
  }

  printOrder(order: WarehouseOrder): void {
    console.log('Tulosta tilaus - Print order:', order);
    this.snackBar.open('Tulostetaan...', 'Sulje', { duration: 2000 });
  }

  resetFilters(): void {
    this.searchControl.setValue('');
    this.statusFilter.setValue([]);
    this.priorityFilter.setValue('');
    this.dataSource.filter = '';
  }

  saveOrder(): void {
    if (this.orderForm.valid) {
      const formValue = this.orderForm.value;
      const newOrder: WarehouseOrder = {
        id: Math.max(...this.sampleOrders.map(o => o.id)) + 1,
        customerName: formValue.basicInfo.customerName,
        description: formValue.basicInfo.description,
        status: 'odottaa',
        itemCount: formValue.basicInfo.itemCount,
        orderDate: new Date(),
        deliveryDate: formValue.locationInfo.deliveryDate,
        priority: formValue.basicInfo.priority,
        location: formValue.locationInfo.location
      };

      this.sampleOrders.push(newOrder);
      this.dataSource.data = [...this.sampleOrders];
      this.orderForm.reset();

      this.snackBar.open('Tilaus tallennettu onnistuneesti!', 'Sulje', {
        duration: 3000,
        panelClass: ['success-snackbar']
      });
    }
  }
}
